/** Conversion Utils: */

const bottomLevelUOLRegexp = /<([o|u]l)[^>]*>((?:(?!<[o|u]l[^>]*>).)+?)<\/\1>/g;
const liTmpIndentRegexp = /<li[^>]*_\$indent\$_="(\d+)"[^>]*>/;
const liAsanaIndentRegexp = /<li[^>]*data-list-indent="(\d+)"[^>]*>/;
const liQlIndentRegexp = /<li[^>]*class="ql-indent-(\d+)"[^>]*>/;
const liTypeRegexp = /<li[^>]*data-list-type="(\w+)"[^>]*>/;
const liRegexp = /<li[^>]*>((?:(?!<\/li[^>]*>).)*?)<\/li>/g;
const uolTagRegexp = /(<(\/?([o|u]l))[^>]*>)/;
const wrappedTopLevelLiRegexp =
  /#\*\$~LIST_START~\$\*#(.+?)#\*\$~LIST_END~\$\*#/g;

const flattenListItems = (htmlContent) => {
  if (!bottomLevelUOLRegexp.test(htmlContent)) {
    return htmlContent;
  }

  const convertedHtml = htmlContent.replaceAll(
    bottomLevelUOLRegexp,
    (_match, listType, innerContent) => {
      const type = listType === 'ul' ? 'bulleted' : 'numbered';
      const result = innerContent.replaceAll(
        liRegexp,
        (listItemMatch, listItemInnerContent) => {
          const currentInternalIndentLevel =
            listItemMatch.match(liTmpIndentRegexp)?.[1];
          const _asanaIndentMatch = listItemMatch.match(liAsanaIndentRegexp);
          const asanaIndent = _asanaIndentMatch?.[1];
          const _quillIndentMatch = listItemMatch.match(liQlIndentRegexp);
          const _quillIndentLevel = _quillIndentMatch?.[1];
          const qlIndent = _quillIndentLevel
            ? Number.parseInt(_quillIndentLevel || 0) + 1
            : undefined;

          const indentToSet = currentInternalIndentLevel
            ? Number.parseInt(currentInternalIndentLevel || 0) + 1
            : 1;

          const currentType = listItemMatch.match(liTypeRegexp)?.[1];

          const typeToSet = currentType || type;

          const isEmptyLine = listItemInnerContent === '<p><br></p>';

          return `<li data-list-type="${typeToSet}" ${
            _asanaIndentMatch?.length || _quillIndentMatch?.length
              ? `data-list-indent="${qlIndent || asanaIndent}"`
              : `_$indent$_="${indentToSet}"`
          }>${isEmptyLine ? '' : listItemInnerContent}</li>`;
        },
      );
      return result;
    },
  );
  return flattenListItems(convertedHtml);
};

const _wrapTopLevelListsInternalState = {
  openedListTypes: [],
};

const wrapTopLevelLists = (
  contentToProcess,
  depth = 0,
  processedContent = '',
  startIndicator = '#*$~LIST_START~$*#',
  endIndicator = '#*$~LIST_END~$*#',
) => {
  const foundUOLTag = contentToProcess.match(uolTagRegexp);
  const isClosingTag = foundUOLTag?.[2]?.[0] === '/';
  const listType = foundUOLTag?.[3];
  let processed = '';
  let currentDepth = depth;
  if (isClosingTag) {
    if (
      _wrapTopLevelListsInternalState.openedListTypes[
        _wrapTopLevelListsInternalState.openedListTypes.length - 1
      ] === listType
    ) {
      _wrapTopLevelListsInternalState.openedListTypes.pop();
    } else {
    }
  } else if (listType) {
    _wrapTopLevelListsInternalState.openedListTypes.push(listType);
  }

  if (
    !foundUOLTag?.length &&
    _wrapTopLevelListsInternalState.openedListTypes.length
  ) {
    processedContent =
      processedContent +
      _wrapTopLevelListsInternalState.openedListTypes
        .reverse()
        .map((listType) => `</${listType}>`)
        .join('') +
      endIndicator;
    _wrapTopLevelListsInternalState.openedListTypes = [];
  }

  if (!foundUOLTag?.length || (isClosingTag && !depth)) {
    return processedContent + contentToProcess;
  }
  const indexOfFoundTag = contentToProcess.indexOf(foundUOLTag[1]);
  const contentBeforeTag = contentToProcess.slice(0, indexOfFoundTag);
  const contentAfterTag = contentToProcess.slice(
    indexOfFoundTag + (foundUOLTag[1]?.length || 0),
  );

  currentDepth += isClosingTag ? -1 : 1;

  if (!isClosingTag && !depth) {
    processed =
      processedContent + contentBeforeTag + startIndicator + foundUOLTag[1];
  } else if (isClosingTag && !currentDepth) {
    processed =
      processedContent + contentBeforeTag + foundUOLTag[1] + endIndicator;
  } else {
    processed = processedContent + contentBeforeTag + foundUOLTag[1];
  }

  return wrapTopLevelLists(
    contentAfterTag,
    currentDepth,
    processed,
    startIndicator,
    endIndicator,
  );
};

export const convertListsToEditorIndentList = (_content) => {
  const fn = '[convertListsToEditorIndentList]:';
  if (!_content) return '';
  // Removing styles might break the built-in parsing logic from the library
  // const contentToLog = _content.replaceAll(/ style="[^"]+"/g, '');
  console.debug(fn, 'content:', _content);
  const content = _content
    .replaceAll(
      /<br class="(?:Apple-interchange-newline|ProseMirror-trailingBreak)">(.*?<\/)/g,
      (_match, afterContent) => afterContent,
    )
    .replaceAll(/<li[^>]*>(?!.*<\/li>)/g, () => '')
    .replace(
      /<([u|o]l)[^>]*>(?!.*<\/\1>).*?(<!--EndFragment--><\/body><\/html>)/,
      (_match, _tag, endingTags) => endingTags,
    )
    .replace(/<p[^>]*><br[^>]*><\/p><!--EndFragment-->/, '<!--EndFragment-->');
  const contentWithWrappedTopLevelLists = wrapTopLevelLists(content);
  return contentWithWrappedTopLevelLists.replaceAll(
    wrappedTopLevelLiRegexp,
    (_match, innerContent) => {
      const result = flattenListItems(innerContent)
        .replaceAll(/<\/?[u|o]l[^>]*>/g, '')
        .replaceAll(
          /(<li[^>]*>.+?)(?:<p><br><\/p>)<\/li>/g,
          (_match, group) => `${group}</li>`,
        )
        .replaceAll(/<br[^>]*><br[^>]*>/g, '<br>');
      return `<ol>${result}</ol>`.replaceAll('_$indent$_', 'data-list-indent');
    },
  );
};

/** Reconversion utils for backward compatibility: */

const reconversionTopLevelListRegexp = /<([u|o]l)[^>]*>(.*?)<\/\1>/g;
const reconversionLiRegexp =
  /<li data-list-indent="(\d+)" data-list-type="(\w+)">(.*?)<\/li>/g;

export const reconvertEditorListsToRegularMarkup = (content) => {
  const fn = '[reconvertEditorListsToRegularMarkup]:';
  console.debug(fn, 'content', content);
  return content.replaceAll(
    reconversionTopLevelListRegexp,
    (_match, _internalType, innerContent) => {
      const openedListTypes = [];
      let currentType;
      let currentIndent = 0;
      const liMatches = innerContent.match(reconversionLiRegexp);
      const matchesCount = liMatches?.length;

      if (!matchesCount) {
        return _match;
      }

      let iteration = 0;

      return innerContent.replaceAll(
        reconversionLiRegexp,
        (_match, indent, type, content) => {
          iteration += 1;
          const indentValue = Number.parseInt(indent || 0);

          let replaceString = _match;

          if (indentValue > currentIndent) {
            currentType = type;
            const tagsToAddCount = indentValue - (currentIndent || 0);
            const tagsToAdd = new Array(tagsToAddCount).fill(
              type === 'bulleted' ? '<ul>' : '<ol>',
            );
            openedListTypes.push(...tagsToAdd);
            currentIndent = indentValue;

            replaceString = `${tagsToAdd.join('')}<li>${content}</li>`;
          }

          if (indentValue < currentIndent) {
            currentType = type;
            const tagsToRemoveCount = indentValue - currentIndent;
            const tagsToClose = openedListTypes.splice(tagsToRemoveCount);
            const tagsToAdd = tagsToClose.reverse().map((tag) => {
              const _tag = tag.split('');
              _tag.splice(1, 0, '/');
              return _tag.join('');
            });
            const activeLevelType = openedListTypes[openedListTypes.length - 1];
            const currentTag = currentType === 'bulleted' ? '<ul>' : '<ol>';

            if (activeLevelType && activeLevelType !== currentTag) {
              const extraTagToClose = openedListTypes.pop();
              const _extraTagToClose = extraTagToClose.split('');
              _extraTagToClose.splice(1, 0, '/');
              tagsToAdd.push(_extraTagToClose.join(''), currentTag);
              openedListTypes.push(currentTag);
            }

            currentIndent = indentValue;

            replaceString = `${tagsToAdd.join('')}<li>${content}</li>`;
          }

          currentType = type;
          currentIndent = indentValue;

          if (iteration === matchesCount) {
            replaceString = `${replaceString}${openedListTypes
              .reverse()
              .map((tag) => {
                const _tag = tag.split('');
                _tag.splice(1, 0, '/');
                return _tag.join('');
              })
              .join('')}`;
          }

          return replaceString;
        },
      );
    },
  );
};

const parser = new DOMParser();

const ALLOWED_ATTRIBUTES = ['data-list-indent', 'data-list-type'];

const getAllSiblings = (el, resultArray = []) => {
  const sibling = el?.nextSibling;

  if (!sibling) {
    return resultArray;
  }

  return getAllSiblings(sibling, [...resultArray, sibling]);
};

const flattenList = (list, indent, type, collector) => {
  const debugExecutionId = Math.trunc(Math.random() * 100000);
  const fn = `[flattenList][${debugExecutionId}]`;
  console.debug(`${fn}: START. indent: ${indent}, type: ${type}`);

  const items = list.querySelectorAll(':scope > *');

  items.forEach((el) => {
    const tagName = el?.tagName?.toLowerCase();
    const isOlOrUl = ['ol', 'ul']?.includes(tagName);
    const isLi = tagName === 'li';

    if (isOlOrUl) {
      const listType = tagName === 'ol' ? 'numbered' : 'bulleted';
      console.debug(
        `${fn}: found list element. Going to call flattenList with indent: ${indent + 1}, type: ${listType}`,
      );
      flattenList(el, indent + 1, listType, collector);
      return;
    }

    let innerPEl;

    if (isLi) {
      let indentToSet = indent;
      let typeToSet = type;

      if (
        el.hasAttribute('data-list-indent') &&
        el.hasAttribute('data-list-type')
      ) {
        indentToSet = el.getAttribute('data-list-indent');
        typeToSet = el.getAttribute('data-list-type');
        console.debug(
          `${fn}: List item already has needed data attributes. Going to use these attributes - indent: ${indentToSet}, type: ${typeToSet}`,
          el,
        );
      } else {
        console.debug(
          `${fn}: Going to create list item with indent: ${indentToSet} and type: ${typeToSet}`,
        );
      }

      const newLi = document.createElement('li');
      newLi.setAttribute('data-list-indent', indentToSet);
      newLi.setAttribute('data-list-type', typeToSet);

      console.debug(
        `${fn}: Going to get the direct <p> or <div> element and replace it with empty if it contains only br in order not to have extra breaks`,
      );

      innerPEl = el.querySelector(':scope > p, :scope > div');
      const isEmptyP =
        innerPEl?.childNodes?.length === 1 &&
        innerPEl?.childNodes?.[0]?.tagName?.toLowerCase() === 'br';

      if (isEmptyP) {
        const newP = document.createElement('p');
        innerPEl.replaceWith(newP);
      }

      console.debug(
        `${fn}: Going to append the direct <p> element to the created list item`,
      );

      if (innerPEl) {
        newLi.appendChild(innerPEl.cloneNode(true));
      } else {
        console.debug(
          `${fn}: No direct <p> elements found, will wrap child content in a new <p>`,
        );
        const newLineP = document.createElement('p');
        Array.from(el.childNodes).forEach((childNode) => {
          const childTag = childNode.tagName?.toLowerCase();
          if (!['ol', 'ul'].includes(childTag)) {
            newLineP.appendChild(childNode.cloneNode(true));
          }
        });
        newLi.appendChild(newLineP);
      }

      collector.push(newLi);
    } else {
      console.warn(
        `${fn}: Not a list or list item element. Going to pass it as is`,
        el,
      );
      collector.push(el);
      return;
    }

    console.debug(`${fn}: Check for nested lists`);
    const subLists = el.querySelectorAll(':scope > ol, :scope > ul');
    subLists.forEach((subList) => {
      const subType =
        subList.tagName.toLowerCase() === 'ol' ? 'numbered' : 'bulleted';
      console.debug(
        `${fn}: found nested list element. Going to call flattenList with indent: ${indent + 1}, type: ${subType}`,
      );
      flattenList(subList, indent + 1, subType, collector);
    });
  });
};

export const convertFromTreeToFlatList = (htmlString, clearDataAttributes) => {
  const fn = '[convertFromTreeToFlatList]';
  try {
    console.debug(`${fn}: string to process:`, htmlString);

    if (!htmlString) {
      console.debug(`${fn}: string is empty!`);
      return '';
    }

    const doc = parser.parseFromString(
      '<body>' + htmlString + '</body>',
      'text/html',
    );

    console.debug(`${fn}: Going to clean up not allowed attributes`);
    doc.querySelectorAll(':not(a)').forEach((el) => {
      [...(el?.attributes || [])].forEach((attr) => {
        if (clearDataAttributes || !ALLOWED_ATTRIBUTES.includes(attr.name)) {
          el.removeAttribute(attr.name);
        }
      });
    });

    doc.querySelectorAll('li > p > br');

    const liOutsideOfList = doc.querySelector('li:not(ul > li):not(ol > li)');

    if (liOutsideOfList) {
      console.debug(`${fn}: Found a list item outside of a list`);
      const wrapperOl = doc.createElement('ol');

      const siblingLiItems = getAllSiblings(liOutsideOfList);

      console.debug(
        `${fn}: Going to wrap this item and it's siblings in list element`,
      );
      liOutsideOfList.parentNode.insertBefore(wrapperOl, liOutsideOfList);

      wrapperOl.appendChild(liOutsideOfList);
      siblingLiItems.forEach((sibling) => {
        wrapperOl.appendChild(sibling);
      });
    }

    const rootLists = Array.from(doc.querySelectorAll('body > ol, body > ul'));

    rootLists.forEach((rootList) => {
      const flattenedItems = [];
      const rootType =
        rootList.tagName.toLowerCase() === 'ol' ? 'numbered' : 'bulleted';

      console.debug(`${fn}: Going to process root level list`, rootList);

      flattenList(rootList, 1, rootType, flattenedItems);

      console.debug(
        `${fn}: Processing of root level list is done. Going to replace it with single list element containing all flattened items`,
      );

      const newRoot = doc.createElement('ol');
      flattenedItems.forEach((li) => {
        newRoot.appendChild(li);
      });
      rootList.replaceWith(newRoot);
    });

    console.debug(`${fn}: after processing:`, doc);

    return doc.body.innerHTML.trim();
  } catch (e) {
    console.warn(e);
    console.warn(
      `${fn}: Failed to convert. Going to pass the content as is`,
      htmlString,
    );
    return htmlString;
  }
};

const buildListTree = (items, index = 0, indent = 1, type) => {
  const debugExecutionId = Math.trunc(Math.random() * 100000);
  const fn = `[buildListTree][${debugExecutionId}]`;
  console.debug(
    `${fn}: START. index: ${index}, indent: ${indent}, items.length: ${items?.length}`,
  );

  if (index >= items?.length) {
    console.warn(
      `${fn}: index is above the overall length of items array. Going to return null.`,
    );
    return null;
  }

  const tmpTreeRoot = document.createElement('div');

  let currentIndex = index;
  let currentType = type;

  while (currentIndex < items?.length) {
    console.debug(
      `${fn}: while loop cycle has started with currentIndex: ${currentIndex}`,
    );

    const liItem = items[currentIndex];
    const liIndent = parseInt(liItem.getAttribute('data-list-indent'), 10) || 1;
    const liType = liItem.getAttribute('data-list-type') || 'numbered';

    if (currentIndex === index) {
      currentType = liType;
      const listWrapperEl = document.createElement(
        liType === 'numbered' ? 'ol' : 'ul',
      );
      console.debug(
        `${fn}: Going to create the list wrapper element for ${liType} list type`,
      );
      tmpTreeRoot.appendChild(listWrapperEl);
    }

    switch (true) {
      case liIndent === indent && liType === currentType: {
        console.debug(
          `${fn}: List item has the same indent and type as the current list. Going to append the list item, currentType: ${currentType}, liType: ${liType}`,
          liItem,
          tmpTreeRoot?.lastElementChild,
        );
        tmpTreeRoot?.lastElementChild?.appendChild(liItem);
        currentIndex = currentIndex + 1;
        break;
      }
      case liIndent === indent && liType !== currentType: {
        console.debug(
          `${fn}: Same indent but defferent list type. Going to call buildListTree to get a sibling list tree`,
        );
        const siblingListTree = buildListTree(items, currentIndex, indent);

        if (siblingListTree) {
          console.debug(
            `${fn}: Sibling list tree was received, Next index will be ${siblingListTree?.nextIndex}`,
          );
          Array.from(siblingListTree?.list?.children || [])?.forEach(
            (siblingList) => {
              tmpTreeRoot?.appendChild(siblingList);
            },
          );
          currentIndex = siblingListTree?.nextIndex;
        }
        break;
      }
      case liIndent < indent: {
        console.debug(
          `${fn}: List item has indent below the current list. Going to return from the current recursion level with tmp result`,
        );
        return { list: tmpTreeRoot, nextIndex: currentIndex };
      }
      case liIndent > indent: {
        console.debug(
          `${fn}: List item has indent above the current list. Going to call buildListTree to get a child list tree`,
        );
        const childListTree = buildListTree(items, currentIndex, indent + 1);

        if (childListTree) {
          console.debug(
            `${fn}: Child list tree was received, Next index will be ${childListTree?.nextIndex}`,
          );

          Array.from(childListTree?.list?.children || [])?.forEach(
            (childList) => {
              (
                tmpTreeRoot?.lastElementChild?.lastElementChild ||
                tmpTreeRoot?.lastElementChild
              )?.appendChild(childList);
            },
          );
          currentIndex = childListTree?.nextIndex;
        }
        break;
      }
      default: {
        console.warn(
          `${fn}: None of conditions matched. This item won't be included`,
          liItem,
        );
      }
    }
  }

  console.debug(
    `${fn}: while loop has finished. Going to return from the current recursion level with tmp result`,
  );
  return { list: tmpTreeRoot, nextIndex: currentIndex };
};

export const reconvertFromFlatToTreeList = (htmlString) => {
  const fn = '[reconvertFromFlatToTreeList]';
  try {
    console.debug(`${fn}: string to process:`, htmlString);
    const doc = parser.parseFromString(
      '<body>' + htmlString + '</body>',
      'text/html',
    );

    const flatLiElement = doc.querySelector('li[data-list-indent]');

    if (!flatLiElement) {
      console.warn(
        `${fn}: No flatten <li> elements found. Going to return htmlString without any processing`,
      );
      return htmlString;
    }

    const rootLists = Array.from(doc.querySelectorAll('body > ol, body > ul'));

    rootLists.forEach((rootList, index) => {
      const listItems = Array.from(rootList.querySelectorAll('li'));

      if (!listItems?.length) return;

      console.debug(`${fn}: Going to process root level list`);
      const { list } = buildListTree(listItems);
      try {
        rootList.replaceWith(...list.childNodes);
      } catch (e) {
        console.warn(e);
        console.warn(
          `${fn}: Failed to replace the rootList with list.childNodes`,
        );
      }
    });

    doc.querySelectorAll('li').forEach((li) => {
      li.removeAttribute('data-list-indent');
      li.removeAttribute('data-list-type');
    });

    console.debug(`${fn}: after processing:`, doc);

    return doc.body.innerHTML.trim();
  } catch (e) {
    console.warn(e);
    console.warn(
      `${fn}: Failed to reconvert. Going to pass the content as is`,
      htmlString,
    );
    return htmlString;
  }
};
